import {
  STOREFRONT_SORTS,
  type MultiSelections,
  type RangeSelections,
  type SmartRank,
  type StorefrontScope,
  type StorefrontSort,
} from './storefront-api'

export type CatalogSearch = {
  q?: string
  page?: number
  sort?: StorefrontSort
  scope?: StorefrontScope
  filters?: MultiSelections
  ranges?: RangeSelections
  /** The search smart filters interpreted (USA-1116). */
  ai?: string
  rank?: SmartRank
}

export type DiscoveryState = {
  query: string
  page: number
  sort: StorefrontSort
  scope?: StorefrontScope
  multiSelections: MultiSelections
  rangeSelections: RangeSelections
  ai?: string
  rank?: SmartRank
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function cleanString(value: unknown, maxLength = 200) {
  return typeof value === 'string' ? value.trim().slice(0, maxLength) : ''
}

function validateScope(value: unknown): StorefrontScope | undefined {
  if (!isRecord(value)) return undefined
  const key = value.key
  const scopeValue = cleanString(value.value)
  if ((key !== 'manufacturer' && key !== 'category') || !scopeValue) return undefined
  return { key, value: scopeValue, label: cleanString(value.label) }
}

function validateFilters(value: unknown): MultiSelections {
  if (!isRecord(value)) return {}
  const filters: MultiSelections = {}
  Object.entries(value).forEach(([key, values]) => {
    const list = (Array.isArray(values) ? values : [values])
      .map((item) => cleanString(item))
      .filter(Boolean)
    if (list.length) filters[key] = [...new Set(list)]
  })
  return filters
}

function validateRanges(value: unknown): RangeSelections {
  if (!isRecord(value)) return {}
  const ranges: RangeSelections = {}
  Object.entries(value).forEach(([key, range]) => {
    if (!isRecord(range)) return
    const min = cleanString(range.min, 40)
    const max = cleanString(range.max, 40)
    if (min || max) ranges[key] = { ...(min ? { min } : {}), ...(max ? { max } : {}) }
  })
  return ranges
}

function validateRank(value: unknown): SmartRank | undefined {
  if (!isRecord(value)) return undefined
  const by = (Array.isArray(value.by) ? value.by : [value.by])
    .map((item) => cleanString(item, 120))
    .filter(Boolean)
  const direction = value.direction === 'asc' ? 'asc' : 'desc'
  if (!by.length) return undefined
  return { by: [...new Set(by)], direction, label: cleanString(value.label, 120) }
}

export function validateCatalogSearch(search: Record<string, unknown>): CatalogSearch {
  const q = cleanString(search.q)
  const page = Number(search.page)
  const sort = STOREFRONT_SORTS.find((value) => value === search.sort)
  const scope = validateScope(search.scope)
  const filters = validateFilters(search.filters)
  const ranges = validateRanges(search.ranges)
  const ai = cleanString(search.ai)
  const rank = validateRank(search.rank)
  return {
    ...(q ? { q } : {}),
    ...(Number.isSafeInteger(page) && page > 1 ? { page } : {}),
    ...(sort && sort !== 'featured' ? { sort } : {}),
    ...(scope ? { scope } : {}),
    ...(Object.keys(filters).length ? { filters } : {}),
    ...(Object.keys(ranges).length ? { ranges } : {}),
    ...(ai ? { ai } : {}),
    ...(rank ? { rank } : {}),
  }
}

export function stateFromSearch(search: CatalogSearch): DiscoveryState {
  return {
    query: search.q ?? '',
    page: search.page ?? 1,
    sort: search.sort ?? 'featured',
    ...(search.scope ? { scope: search.scope } : {}),
    multiSelections: search.filters ?? {},
    rangeSelections: search.ranges ?? {},
    ...(search.ai ? { ai: search.ai } : {}),
    ...(search.rank ? { rank: search.rank } : {}),
  }
}

export function searchFromState(state: DiscoveryState): CatalogSearch {
  return validateCatalogSearch({
    q: state.query,
    page: state.page,
    sort: state.sort,
    scope: state.scope,
    filters: state.multiSelections,
    ranges: state.rangeSelections,
    ai: state.ai,
    rank: state.rank,
  })
}

export function activeFilterCount(state: DiscoveryState) {
  return (
    Object.values(state.multiSelections).reduce((total, values) => total + values.length, 0) +
    Object.values(state.rangeSelections).filter((range) => range.min || range.max).length
  )
}
